import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import HUD from "@/components/HUD";
import CallQueue from "@/components/CallQueue";
import FleetPanel from "@/components/FleetPanel";
import MobileFabBar, { MobilePanelDrawer } from "@/components/MobileFabBar";
import RotatePhoneOverlay from "@/components/RotatePhoneOverlay";
import { IosInstallHint, LaunchFullscreenPrompt } from "@/components/FullscreenControls";
import { useIsTabletOrBelow } from "@/hooks/useMediaQuery";
import MapView from "@/components/MapView";
import StationDialog from "@/components/StationDialog";
import HospitalDialog from "@/components/HospitalDialog";
import TransportDialog from "@/components/TransportDialog";
import CallDialog from "@/components/CallDialog";
import RadioPanel from "@/components/RadioPanel";
import MutualAidDialog from "@/components/MutualAidDialog";
import HelpRequestsPanel from "@/components/HelpRequestsPanel";
import BuyGemsDialog from "@/components/BuyGemsDialog";
import DailyBonusDialog from "@/components/DailyBonusDialog";
import AchievementsDialog from "@/components/AchievementsDialog";
import SettingsDialog from "@/components/SettingsDialog";
import DeveloperDialog from "@/components/DeveloperDialog";
import FaqHelpDialog from "@/components/FaqHelpDialog";
import NewsDialog from "@/components/NewsDialog";
import FriendsDialog from "@/components/FriendsDialog";
import useFriendNotifications from "@/hooks/useFriendNotifications";
import ProfileDialog from "@/components/ProfileDialog";
import SharedCallDialog from "@/components/SharedCallDialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Diamond } from "@phosphor-icons/react";
import {
  addStation,
  placeHospital,
  reserveBed,
  admitPatient,
  placeAllianceHospital,
  listAllianceHospitals,
  allianceStationDetail,
  callResolved,
  getState,
  getGemCheckoutStatus,
  getDailyStatus,
  recordGameEvent,
  quickFinishCall as quickFinishCallApi,
  helperUpdate as apiHelperUpdate,
  sharePatientTransport,
  listEligibleHospitals,
  listMissions,
  listActiveEvents,
  getGameVersion,
  getNewsUnread,
} from "@/lib/api";
import { xpForCall } from "@/lib/ranks";
import { patientReward, hydrateEmergencyTypes, setActiveEventMissionIds } from "@/lib/gameConfig";
import { toast } from "sonner";
import {
  STATION_COST,
  STARTING_MONEY,
  distanceKm,
  gemCost,
  quickFinishGems,
  stationCost,
  EMERGENCY_TYPES,
} from "@/lib/gameConfig";
import { US_CITIES } from "@/lib/cities";
import { getRoute, reverseRoute } from "@/lib/routing";
import { isSoundEnabled, setSoundEnabled } from "@/lib/sound";
import useGameEngine from "@/hooks/useGameEngine";
import useMutualAid from "@/hooks/useMutualAid";
import { bumpCallIdAtLeast, bumpDispatchIdAtLeast, nextDispatchId } from "@/hooks/gameIds";

let RADIO_ID = 1;
const RADIO_LOG_CAP = 80;

function buildApparatusState(stations) {
  const state = {};
  stations.forEach((s) => {
    (s.trucks || []).forEach((t) => {
      state[t.id] = {
        stationId: s.id,
        name: t.name,
        type: t.type || "Engine",
        available: true,
      };
    });
  });
  return state;
}

export default function Game({ user, onLogout }) {
  const username = user?.name || user?.email || "Dispatcher";
  const [player, setPlayer] = useState({
    username,
    money: STARTING_MONEY,
    gems: 0,
  });
  const [stations, setStations] = useState([]);
  const [hospitals, setHospitals] = useState([]);
  const [allianceHospitals, setAllianceHospitals] = useState([]);
  const [mainStationId, setMainStationId] = useState(null);
  const [stationZoom, setStationZoom] = useState(13);
  const [spawnIntervalMs, setSpawnIntervalMs] = useState(60000);
  // Full preferences object (drives Settings dialog + auto-transport logic).
  // Kept in sync with server `state.preferences` on every load / patch.
  const [preferences, setPreferences] = useState({
    station_zoom: 13,
    spawn_interval_ms: 60000,
    auto_transport_enabled: false,
    auto_transport_mode: "closest",
    auto_clear_medics_enabled: false,
    cad_icons_enabled: true,
    cad_icons_show_full_name: false,
  });
  const [isPremium, setIsPremium] = useState(false);
  const [apparatusState, setApparatusState] = useState({});
  const [calls, setCalls] = useState([]);
  const [dispatches, setDispatches] = useState([]);
  // Follow-Up queue: `{ [callId]: string[] }` — apparatus IDs that the user
  // has pre-assigned to respond to `callId` as soon as they finish their
  // current call. Persisted per-user in localStorage so a reload doesn't
  // drop queued responders. Consumed by the effect further down that
  // watches `apparatusState` and auto-dispatches when a queued unit
  // becomes available.
  const [followUpQueue, setFollowUpQueue] = useState({});
  const [placeMode, setPlaceMode] = useState(false);
  const [activeEvents, setActiveEvents] = useState([]);
  const [placeModePayWith, setPlaceModePayWith] = useState("money");
  const [placePoliceMode, setPlacePoliceMode] = useState(false);
  const [placePolicePayWith, setPlacePolicePayWith] = useState("money");
  const [placeHospitalMode, setPlaceHospitalMode] = useState(false);
  const [placeHospitalPayWith, setPlaceHospitalPayWith] = useState("money");
  // 'player' (default) | 'alliance' (placement is funded by the alliance bank)
  const [placeHospitalKind, setPlaceHospitalKind] = useState("player");
  const [loaded, setLoaded] = useState(false);
  const [dialogStation, setDialogStation] = useState(null);
  const [stationDialogOpen, setStationDialogOpen] = useState(false);
  const [dialogHospital, setDialogHospital] = useState(null);
  const [hospitalDialogOpen, setHospitalDialogOpen] = useState(false);
  // Read-only Mutual Aid Group view (alliance hospitals & member stations).
  const [viewOnlyMode, setViewOnlyMode] = useState(false);
  const [viewOnlyOwnerName, setViewOnlyOwnerName] = useState(null);
  const [transportPayload, setTransportPayload] = useState(null); // {patient, call, share?}
  const [transportDialogOpen, setTransportDialogOpen] = useState(false);
  const [activeCall, setActiveCall] = useState(null);
  const [callDialogOpen, setCallDialogOpen] = useState(false);
  const [soundOn, setSoundOn] = useState(isSoundEnabled());
  const [radioLog, setRadioLog] = useState([]);

  // ---- Mass dispatch / recall staggering ----
  // When the player rapid-fires several +1 NEAREST clicks (or hits "Clear All"
  // on a full station), we serialise the resulting truck animations so the
  // map stays readable. Each operation gets its own queue keyed by intent:
  //   - dispatches stagger off `nextDispatchAtRef`
  //   - recalls    stagger off `nextRecallAtRef`
  // The constant is intentionally small (≈3 s — matches OSRM-routed truck
  // marker visibility) — operations queued well past that window get a toast
  // letting the player know it's been queued rather than dropped.
  const STAGGER_MS = 3000;
  const nextDispatchAtRef = useRef(0);
  const nextRecallAtRef = useRef(0);
  const scheduleStaggered = useCallback((slotRef, fn) => {
    const now = Date.now();
    const runAt = Math.max(now, slotRef.current);
    const delay = runAt - now;
    slotRef.current = runAt + STAGGER_MS;
    if (delay <= 0) fn();
    else setTimeout(fn, delay);
    return delay; // ms — caller may surface as a "QUEUED Xs" countdown
  }, []);

  const pushRadio = useCallback((kind, text) => {
    setRadioLog((prev) => {
      const next = [...prev, { id: RADIO_ID++, ts: Date.now(), kind, text }];
      if (next.length > RADIO_LOG_CAP) next.splice(0, next.length - RADIO_LOG_CAP);
      return next;
    });
  }, []);

  const ref = useRef({});
  const loaderIntervalsRef = useRef([]);
  // Mutate rather than replace so fields written from OTHER hooks (e.g.
  // useMutualAid's `shares`, `userId`, `allianceId`) persist across Game's
  // own re-renders. Replacing `ref.current` here wiped those mid-tick and
  // caused the game engine to see zero alliance helpers on scene — which
  // broke the mission-timer pause logic and the "helpers count toward
  // required units" check.
  ref.current.player = player;
  ref.current.stations = stations;
  ref.current.hospitals = hospitals;
  ref.current.allianceHospitals = allianceHospitals;
  ref.current.apparatusState = apparatusState;
  ref.current.calls = calls;
  ref.current.dispatches = dispatches;
  ref.current.loaded = loaded;
  ref.current.placeMode = placeMode;
  ref.current.placeModePayWith = placeModePayWith;
  ref.current.placePoliceMode = placePoliceMode;
  ref.current.placePolicePayWith = placePolicePayWith;
  ref.current.placeHospitalMode = placeHospitalMode;
  ref.current.placeHospitalPayWith = placeHospitalPayWith;
  ref.current.placeHospitalKind = placeHospitalKind;

  // ---------- Load saved state (player + stations + restorable mid-flight calls/dispatches) ----------
  // CRITICAL: we ONLY set `loaded = true` after a SUCCESSFUL state fetch.
  // If we set it after a failure, the snapshot heartbeat would start
  // firing with the default STARTING_MONEY ($50k) and overwrite the
  // player's real balance in MongoDB. This was the root cause of
  // reported "money reset to $50,000" incidents.
  const [loadError, setLoadError] = useState(null);
  const [loadRetry, setLoadRetry] = useState(0);
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        // Hydrate the mission catalog from MongoDB FIRST so the engine + UI
        // see any dev-edited / newly-added missions before the first tick.
        // Missing endpoint or network error → fall back to the in-repo
        // defaults already compiled into gameConfig.js (no-op).
        try {
          const missionsResp = await listMissions();
          if (missionsResp?.missions?.length) {
            hydrateEmergencyTypes(missionsResp.missions);
          }
        } catch (_e) {
          /* defaults already in place — never block the load on this */
        }
        // Prime the active-event mission whitelist so the spawn pool
        // reflects any live event immediately. Failures are non-fatal —
        // event_only missions simply stay locked when we can't reach the
        // endpoint (that's the safe default).
        try {
          const ev = await listActiveEvents();
          const ids = [];
          (ev?.events || []).forEach((e) => (e.mission_ids || []).forEach((id) => ids.push(id)));
          setActiveEventMissionIds(ids);
          setActiveEvents(ev?.events || []);
        } catch (_e) {
          setActiveEventMissionIds([]);
          setActiveEvents([]);
        }
        // Refresh active events every 60s so newly-started events light up
        // the banner without a page reload (and expired ones drop off).
        const evPollId = setInterval(async () => {
          try {
            const ev = await listActiveEvents();
            const ids = [];
            (ev?.events || []).forEach((e) => (e.mission_ids || []).forEach((id) => ids.push(id)));
            setActiveEventMissionIds(ids);
            setActiveEvents(ev?.events || []);
          } catch (_e) { /* silent — banner just doesn't refresh */ }
        }, 60_000);
        // Stash on the loader closure's cancellation path — the outer
        // effect's `return () => { cancelled = true }` will fire only
        // once the whole load unmounts.
        loaderIntervalsRef.current.push(evPollId);
        const resp = await getState();
        if (cancelled) return;
        const s = resp.state || {};
        setPlayer({
          username,
          money: s.money ?? STARTING_MONEY,
          gems: s.gems ?? 0,
          donation_pct: s.donation_pct || 0,
          hospital_fee_pct: s.hospital_fee_pct || 0,
        });
        const sts = s.stations || [];
        setStations(sts);
        setHospitals(s.hospitals || []);
        setMainStationId(s.main_station_id || sts[0]?.id || null);
        if (s.preferences?.station_zoom) {
          setStationZoom(s.preferences.station_zoom);
        }
        if (s.preferences?.spawn_interval_ms) {
          setSpawnIntervalMs(s.preferences.spawn_interval_ms);
        }
        // Hydrate full preferences object so Settings dialog + auto-transport
        // engine effect have the current truth on first mount.
        if (s.preferences) {
          setPreferences((prev) => ({ ...prev, ...s.preferences }));
        }
        // Premium status — backend auto-expires; we just read the boolean.
        if (s.premium_until) {
          try {
            setIsPremium(new Date(s.premium_until) > new Date());
          } catch (_e) {
            setIsPremium(false);
          }
        }
        const apState = buildApparatusState(sts);
        // Restore active dispatches so we can mark units busy
        const restoredDispatches = (s.active_dispatches || []).map((d) => ({ ...d }));
        let maxDispatchId = 0;
        restoredDispatches.forEach((d) => {
          if (typeof d.id === "number" && d.id > maxDispatchId) maxDispatchId = d.id;
          if (d.apparatusId && apState[d.apparatusId] && !d.returning) {
            apState[d.apparatusId] = { ...apState[d.apparatusId], available: false };
          }
        });
        bumpDispatchIdAtLeast(maxDispatchId);
        setApparatusState(apState);
        setDispatches(restoredDispatches);

        const restoredCalls = (s.active_calls || []).map((c) => ({ ...c }));
        let maxCallId = 0;
        restoredCalls.forEach((c) => {
          if (typeof c.id === "number" && c.id > maxCallId) maxCallId = c.id;
        });
        bumpCallIdAtLeast(maxCallId);
        setCalls(restoredCalls);
        setLoadError(null);
        setLoaded(true);
      } catch (e) {
        console.error("Game state load failed:", e);
        if (cancelled) return;
        setLoadError(e?.message || "Could not load saved game");
        // DO NOT set loaded = true here — that would start the snapshot
        // heartbeat with the default $50k money and erase the player's
        // real balance in MongoDB. Stay on the loading/retry screen
        // until a successful fetch.
      }
    })();
    return () => {
      cancelled = true;
      // Clear any polling intervals kicked off inside the loader
      // (currently: the active-events poll). Guard against future
      // additions by iterating rather than tracking by name.
      loaderIntervalsRef.current.forEach((id) => clearInterval(id));
      loaderIntervalsRef.current = [];
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loadRetry]);

  // Stable callback that reads the latest fireGameEvent via a ref — prevents
  // the engine hook from tearing down its 500 ms interval every render.
  const fireGameEventRef = useRef(null);
  const handleCallResolvedRef = useRef(null);
  // Ref-backed follow-up handler: reads the latest queue + calls each
  // synchronous engine tick. We keep it out of the useCallback below so
  // this file's `followUpQueueRef` / `handleDispatchUnit` can be captured
  // after they're declared without stale closures.
  const followUpOnResolvedRef = useRef(null);
  const handleCallResolved = useCallback((resolvedCalls) => {
    const calls = Array.isArray(resolvedCalls) ? resolvedCalls : [];
    const count = calls.length || 1;
    const xpTotal = calls.reduce((acc, c) => acc + xpForCall(c?.type), 0);
    fireGameEventRef.current?.("calls_resolved", count, xpTotal || undefined);
    handleCallResolvedRef.current?.(calls);
    // Follow-Up auto-dispatch: fires once per resolve, DIRECTLY from the
    // engine bridge — avoids apparatusState-transition-watching (which was
    // brittle, missed some resolve paths, and could purge queue entries
    // on the wrong trigger). See followUpOnResolvedRef initialization
    // further down.
    followUpOnResolvedRef.current?.(calls);
  }, []);

  // Same pattern for call-upgraded — the real handler is wired up later (it
  // needs `alliance` + `sharedByCall` which are declared further down).
  const handleCallUpgradedRef = useRef(null);
  const handleCallUpgraded = useCallback((callId) => {
    handleCallUpgradedRef.current?.(callId);
  }, []);

  // ---------- Game engine (5 setInterval timers extracted into a hook) ----------
  const { spawnCall } = useGameEngine({
    loaded,
    stateRef: ref,
    setPlayer,
    setCalls,
    setDispatches,
    setApparatusState,
    setStations,
    pushRadio,
    onCallResolved: handleCallResolved,
    onCallUpgraded: handleCallUpgraded,
    spawnIntervalMs,
  });

  // ---------- Game-event firing for retention/achievement counters ----------
  // Best-effort: server validates type, returns unlocked achievements & gem credit.
  const fireGameEvent = useCallback((type, count = 1, xp = undefined) => {
    recordGameEvent(type, count, xp)
      .then((r) => {
        if (r.newly_unlocked && r.newly_unlocked.length > 0) {
          setPlayer((p) => ({ ...p, gems: r.current_gems }));
          r.newly_unlocked.forEach((a) => {
            pushRadio("achievement", `🏆 ${a.title} — ${a.desc} (+${a.reward} gems)`);
          });
        }
        if (r.rank_up && r.rank?.label) {
          pushRadio("achievement", `★ Promoted to ${r.rank.label}!`);
        }
      })
      .catch(() => {});
  }, [pushRadio]);
  useEffect(() => {
    fireGameEventRef.current = fireGameEvent;
  }, [fireGameEvent]);

  // ---------- Build station (two-phase: map-click opens confirm dialog) ----------
  const [pendingBuild, setPendingBuild] = useState(null); // {lat, lng, name, cost, payWith, gCost}

  const handleMapClick = useCallback(
    (latlng) => {
      // Hospital placement is checked first because the toggle key is distinct
      // from station placement and the hospital cost is much higher.
      if (ref.current.placeHospitalMode) {
        const lat = latlng.lat, lng = latlng.lng;
        if (lat < 18 || lat > 72 || lng < -170 || lng > -66) {
          toast.error("Hospitals must be placed inside US territory.");
          return;
        }
        const kind = ref.current.placeHospitalKind || "player";
        const cost = 1_000_000;
        const nearest = US_CITIES
          .map((c) => ({ c, d: distanceKm(c, { lat, lng }) }))
          .sort((a, b) => a.d - b.d)[0];
        const name = `${nearest.c.name.split(",")[0]} General Hospital`;
        if (kind === "alliance") {
          if (!ref.current.alliance) { toast.error("Join an alliance first."); return; }
          const allianceHospitalCount = (ref.current.alliance.hospitals || []).length;
          const allianceCost = allianceHospitalCount === 0 ? 0 : cost;
          (async () => {
            try {
              await placeAllianceHospital(ref.current.alliance.id, { name, lat, lng });
              pushRadio("build", allianceCost === 0
                ? `${name} built by alliance (first hospital — FREE)`
                : `${name} built by alliance (-$${allianceCost.toLocaleString()} from bank)`);
              setPlaceHospitalMode(false);
              setPlaceHospitalKind("player");
            } catch (e) {
              toast.error(e?.response?.data?.detail || "Could not build alliance hospital");
            }
          })();
          return;
        }
        const payWith = ref.current.placeHospitalPayWith || "money";
        if (payWith === "gems") {
          const gCost = gemCost(cost);
          if ((ref.current.player.gems ?? 0) < gCost) {
            toast.error(`Need ${gCost.toLocaleString()} gems to build a hospital.`);
            return;
          }
        } else if ((ref.current.player.money ?? 0) < cost) {
          toast.error(`Need $${cost.toLocaleString()} to build a hospital.`);
          return;
        }
        (async () => {
          try {
            const updated = await placeHospital({ name, lat, lng, pay_with: payWith });
            setHospitals(updated.hospitals || []);
            setPlayer((pl) => ({ ...pl, money: updated.money, gems: updated.gems }));
            const label = payWith === "gems"
              ? `-◆${gemCost(cost).toLocaleString()} gems`
              : `-$${cost.toLocaleString()}`;
            pushRadio("build", `${name} built (${label})`);
            setPlaceHospitalMode(false);
          } catch (e) {
            toast.error(e?.response?.data?.detail || "Could not build hospital");
          }
        })();
        return;
      }
      if (!ref.current.placeMode && !ref.current.placePoliceMode) return;
      const isPolice = !!ref.current.placePoliceMode;
      // Police stations count separately for tier pricing so they don't
      // pile onto the fire-station price ladder.
      const owned = isPolice
        ? ref.current.stations.filter((s) => (s.kind || "fire") === "police").length
        : ref.current.stations.filter((s) => (s.kind || "fire") === "fire").length;
      const cost = isPolice ? 500_000 : stationCost(owned);
      const payWith = isPolice
        ? (ref.current.placePolicePayWith || "money")
        : (ref.current.placeModePayWith || "money");
      const lat = latlng.lat;
      const lng = latlng.lng;
      if (lat < 18 || lat > 72 || lng < -170 || lng > -66) {
        toast.error("Stations must be placed inside US territory.");
        return;
      }
      if (payWith === "gems") {
        const gCost = gemCost(cost);
        if ((ref.current.player.gems ?? 0) < gCost) {
          toast.error(`Need ${gCost.toLocaleString()} gems for ${isPolice ? "police " : ""}station #${owned + 1}.`);
          return;
        }
      } else if (ref.current.player.money < cost) {
        toast.error(`Need $${cost.toLocaleString()} for ${isPolice ? "police " : ""}station #${owned + 1}.`);
        return;
      }
      const nearest = US_CITIES
        .map((c) => ({ c, d: distanceKm(c, { lat, lng }) }))
        .sort((a, b) => a.d - b.d)[0];
      const prefix = isPolice ? "PS" : "FS";
      const name = `${prefix} ${(owned + 1).toString().padStart(2, "0")} ${nearest.c.name.split(",")[0]}`;
      setPendingBuild({ lat, lng, name, cost, payWith, gCost: gemCost(cost), cityName: nearest.c.name, kind: isPolice ? "police" : "fire" });
    },
    [pushRadio]
  );

  const cancelBuild = useCallback(() => setPendingBuild(null), []);

  const confirmBuild = useCallback(async () => {
    const p = pendingBuild;
    if (!p) return;
    try {
      const updated = await addStation({ name: p.name, lat: p.lat, lng: p.lng, pay_with: p.payWith, kind: p.kind || "fire" });
      setStations(updated.stations);
      const newStation = updated.stations[updated.stations.length - 1];
      setApparatusState((prev) => {
        const next = { ...prev };
        (newStation.trucks || []).forEach((t) => {
          next[t.id] = { stationId: newStation.id, name: t.name, type: t.type || "Engine", available: true };
        });
        return next;
      });
      setPlayer((pl) => ({ ...pl, money: updated.money, gems: updated.gems }));
      if (updated.main_station_id) {
        setMainStationId(updated.main_station_id);
      }
      const costLabel = p.payWith === "gems"
        ? `-◆${p.gCost.toLocaleString()} gems`
        : `-$${p.cost.toLocaleString()}`;
      pushRadio("build", `${newStation.name} built (${costLabel})`);
      fireGameEvent("stations_built", 1);
      setPlaceMode(false);
      setPlaceModePayWith("money");
      setPlacePoliceMode(false);
      setPlacePolicePayWith("money");
      setPendingBuild(null);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not build station");
      setPendingBuild(null);
    }
  }, [pendingBuild, fireGameEvent, pushRadio]);

  // ---------- Dispatch a specific unit to a call ----------
  const handleDispatchUnit = useCallback((callId, apparatusId) => {
    // Snapshot validation up front so the UI rejects obviously-bad requests
    // synchronously (truck already busy, call gone) — only the actual
    // movement-causing work is staggered.
    const call = ref.current.calls.find((c) => c.id === callId);
    if (!call) return;
    const aState = ref.current.apparatusState[apparatusId];
    if (!aState || !aState.available) {
      toast.error("Unit not available.");
      return;
    }
    // Home base may be a station OR a hospital (hospital bays house Medics).
    const homeStation = aState.stationId
      ? ref.current.stations.find((s) => s.id === aState.stationId)
      : null;
    const homeHospital = aState.hospitalId
      ? ref.current.hospitals.find((h) => h.id === aState.hospitalId)
      : null;
    const home = homeStation || homeHospital;
    if (!home) return;
    // Reserve the apparatus IMMEDIATELY (so a second click can't pick the
    // same truck). The actual movement + route + dispatch row creation are
    // deferred via scheduleStaggered so mass-dispatches don't all start
    // moving at the same instant.
    setApparatusState((as) => ({
      ...as,
      [apparatusId]: { ...as[apparatusId], available: false },
    }));

    // Pre-allocate the dispatch id so we can render a placeholder NOW and
    // upgrade it to a real moving row later. The CallDialog's DispatchedList
    // will see this row right away and show a "QUEUED 3s" countdown instead
    // of making the player wait 3 seconds to see anything happen.
    const dispatchId = nextDispatchId();
    const queuedAt = Date.now();

    const delay = scheduleStaggered(nextDispatchAtRef, async () => {
      // Re-read at execution time — the world may have changed (call
      // resolved, station deleted) while this op was queued.
      const liveCall = ref.current.calls.find((c) => c.id === callId);
      const liveHome = aState.stationId
        ? ref.current.stations.find((s) => s.id === aState.stationId)
        : ref.current.hospitals.find((h) => h.id === aState.hospitalId);
      if (!liveCall || !liveHome) {
        // Refund the apparatus reservation + drop the placeholder if the
        // op can no longer run.
        setApparatusState((as) => ({
          ...as,
          [apparatusId]: { ...as[apparatusId], available: true },
        }));
        setDispatches((ds) => ds.filter((x) => x.id !== dispatchId));
        return;
      }
      const returningTrip = ref.current.dispatches.find(
        (d) => d.apparatusId === apparatusId && d.returning
      );
      const origin = returningTrip
        ? { lat: returningTrip.lat, lng: returningTrip.lng }
        : { lat: liveHome.lat, lng: liveHome.lng };
      if (returningTrip) {
        setDispatches((ds) => ds.filter((d) => d.id !== returningTrip.id));
      }
      const route = await getRoute(origin, { lat: liveCall.lat, lng: liveCall.lng }, { apparatusType: aState.type });
      pushRadio(
        "dispatch",
        returningTrip
          ? `${aState.name} diverted to ${liveCall.address || "new call"}`
          : `${aState.name} responding from ${liveHome.name} to ${liveCall.address || "scene"}`
      );
      // Upgrade the placeholder in place (same id) so React doesn't unmount
      // its row — the UI smoothly transitions from "QUEUED" to ETA.
      setDispatches((ds) =>
        ds.map((x) =>
          x.id === dispatchId
            ? {
                ...x,
                queued: false,
                queuedAt: undefined,
                queuedUntil: undefined,
                routeCoords: route.coords,
                lat: route.coords[0][0],
                lng: route.coords[0][1],
                toLat: liveCall.lat,
                toLng: liveCall.lng,
                startedAt: Date.now(),
                durationSec: route.durationSec,
                outboundDurationSec: route.durationSec,
              }
            : x
        )
      );
    });

    // Insert the placeholder right after the schedule call so we know the
    // `delay` we just got. Engine + MapView both skip rows where `queued`
    // is true; CallDialog uses `queuedUntil` to render the countdown.
    setDispatches((ds) => [
      ...ds,
      {
        id: dispatchId,
        stationId: homeStation ? home.id : null,
        hospitalId: homeHospital ? home.id : null,
        apparatusId,
        apparatusName: aState.name,
        apparatusType: aState.type || "Engine",
        callId: call.id,
        onScene: false,
        returning: false,
        queued: delay > 0,
        queuedAt,
        queuedUntil: queuedAt + delay,
        // Placeholder geometry — never rendered because `queued` skips the
        // marker, but kept consistent for any code that inspects lat/lng.
        routeCoords: [[home.lat, home.lng], [call.lat, call.lng]],
        lat: home.lat,
        lng: home.lng,
        toLat: call.lat,
        toLng: call.lng,
        startedAt: queuedAt + delay,
        durationSec: 60,
      },
    ]);
  }, [pushRadio, scheduleStaggered]);

  // ---------- Cancel/recall a specific dispatch back to its station ----------
  // NOTE: We deliberately allow recalling on-scene units even when they're
  // currently needed for the mission. The engine's mega-tick automatically
  // pauses the call's WORKING SCENE timer whenever requirements drop below
  // threshold, so missions "freeze" until the player dispatches replacements
  // — instead of refusing the recall outright.
  const handleCancelDispatch = useCallback((dispatchId) => {
    const d = ref.current.dispatches.find((x) => x.id === dispatchId);
    if (!d || d.returning) return;
    const home = d.stationId
      ? ref.current.stations.find((s) => s.id === d.stationId)
      : ref.current.hospitals.find((h) => h.id === d.hospitalId);
    if (!home) return;
    scheduleStaggered(nextRecallAtRef, () => {
      // Re-read the dispatch at execution time — it may already be returning
      // / resolved by the time the queue ticks down.
      const live = ref.current.dispatches.find((x) => x.id === dispatchId);
      if (!live || live.returning) return;
      const newCoords = live.onScene
        ? reverseRoute({ coords: live.routeCoords }).coords
        : [[live.lat, live.lng], [home.lat, home.lng]];
      const newDuration = live.onScene
        ? (live.outboundDurationSec || live.durationSec)
        : Math.max(5, (live.durationSec || 30) * 0.5);
      setDispatches((ds) =>
        ds.map((x) =>
          x.id === dispatchId
            ? {
                ...x,
                returning: true,
                onScene: false,
                callId: null,
                routeCoords: newCoords,
                startedAt: Date.now(),
                durationSec: newDuration,
              }
            : x
        )
      );
      setApparatusState((as) => ({
        ...as,
        [live.apparatusId]: { ...as[live.apparatusId], available: true },
      }));
      pushRadio("recall", `${live.apparatusName} recalled to ${home.name}`);
      // If this is a helper dispatch on a shared mutual-aid call, notify the
      // requester's server-side share row so the host's CallDialog stops
      // showing the unit as "responding" with a runaway timer.
      if (live.helper && live.sharedId) {
        const aid = ref.current.allianceId;
        if (aid) {
          apiHelperUpdate(aid, live.sharedId, {
            dispatchId: live.id,
            on_scene: false,
            returning: true,
          }).catch(() => {});
        }
      }
    }, `${d.apparatusName} recall`);
  }, [pushRadio, scheduleStaggered]);

  // ---------- Jail transport: send detained arrestee to nearest free cell ----------
  // Picks the closest on-scene Patrol Car / Deputy for the call, then the
  // closest player-owned police station with a free jail cell (capacity
  // minus already-occupied). Kicks off a transport leg identical in shape
  // to the medic → hospital flow (transporting=true → arrival → unload →
  // return). The engine's `delivered` bridge (useGameEngine.js) admits
  // the arrestee to the server on arrival.
  const performJailTransport = useCallback(async ({ callId, arresteeId }) => {
    const call = ref.current.calls.find((c) => c.id === callId);
    if (!call) return;
    const arrestee = (call.arrestees || []).find((a) => a.id === arresteeId);
    if (!arrestee) return;
    if (arrestee.status !== "detained") {
      toast.error("Suspect isn't detained yet");
      return;
    }
    const candidates = ref.current.dispatches.filter((d) =>
      d.callId === callId &&
      d.onScene &&
      !d.transporting &&
      !d.returning &&
      (d.apparatusType === "Patrol Car" || d.apparatusType === "Deputy")
    );
    if (candidates.length === 0) {
      toast.error("Need a Patrol Car or Deputy on scene");
      return;
    }
    const carrier = candidates[0];
    const nowIso = Date.now();
    const police = ref.current.stations.filter((s) => (s.kind || "fire") === "police");
    const stationsWithSpace = police
      .map((s) => {
        const occupied = (s.occupied_jail_cells || []).filter((oc) => {
          try { return new Date(oc.freed_at).getTime() > nowIso; } catch { return false; }
        }).length;
        const capacity = s.jail_cells || 0;
        return { s, free: capacity - occupied };
      })
      .filter((x) => x.free > 0)
      .map((x) => ({ ...x, dist: distanceKm(x.s, call) }))
      .sort((a, b) => a.dist - b.dist);
    if (stationsWithSpace.length === 0) {
      toast.error("No police station has a free jail cell");
      return;
    }
    const target = stationsWithSpace[0].s;
    let route;
    try {
      route = await getRoute({ lat: call.lat, lng: call.lng }, { lat: target.lat, lng: target.lng }, { apparatusType: carrier.apparatusType });
    } catch (_e) {
      route = { coords: [[call.lat, call.lng], [target.lat, target.lng]], durationSec: 120 };
    }
    const carrierHome = ref.current.stations.find((s) => s.id === carrier.stationId);
    if (!carrierHome) { toast.error("Carrier has no home station"); return; }
    setDispatches((ds) => ds.map((x) => x.id === carrier.id ? {
      ...x,
      transporting: true,
      onScene: false,
      returning: false,
      queued: false,
      routeCoords: route.coords,
      lat: route.coords[0][0],
      lng: route.coords[0][1],
      startedAt: Date.now(),
      durationSec: route.durationSec,
      outboundDurationSec: route.durationSec,
      transportArresteeId: arresteeId,
      transportPoliceStationId: target.id,
      transportPoliceStationName: target.name,
      transportHomeLat: carrierHome.lat,
      transportHomeLng: carrierHome.lng,
      transportHomeId: carrierHome.id,
      transportHomeKind: "station",
      callId: null,
    } : x));
    setCalls((prev) => prev.map((c) => c.id !== callId ? c : {
      ...c,
      arrestees: (c.arrestees || []).map((a) =>
        a.id === arresteeId ? { ...a, status: "in_transit", destinationStationId: target.id } : a
      ),
    }));
    pushRadio("dispatch", `${carrier.apparatusName} transporting suspect to ${target.name}`);
  }, [pushRadio]);

  // ---------- Auto-dispatch nearest available unit (CallQueue convenience) ----------
  const handleAutoDispatch = useCallback(async (callId) => {
    const call = ref.current.calls.find((c) => c.id === callId);
    if (!call) return;
    // Map apparatusId -> live origin (current position if mid-return, else station).
    const returningByApp = {};
    ref.current.dispatches.forEach((d) => {
      if (d.returning) returningByApp[d.apparatusId] = { lat: d.lat, lng: d.lng };
    });
    const candidates = [];
    const addFrom = (s) => {
      (s.trucks || []).forEach((tr) => {
        if (ref.current.apparatusState[tr.id]?.available) {
          const origin = returningByApp[tr.id] || { lat: s.lat, lng: s.lng };
          candidates.push({
            apparatusId: tr.id,
            type: tr.type,
            station: s,
            origin,
            dKm: distanceKm(origin, call),
          });
        }
      });
    };
    ref.current.stations.forEach(addFrom);
    (ref.current.hospitals || []).forEach(addFrom);
    if (candidates.length === 0) {
      toast.error("No available units.");
      return;
    }
    // Routed nearest: top 5 by straight-line, then pick smallest OSRM duration.
    candidates.sort((a, b) => a.dKm - b.dKm);
    const top = candidates.slice(0, 5);
    try {
      const routes = await Promise.all(
        top.map((c) => getRoute(c.origin, { lat: call.lat, lng: call.lng }, { apparatusType: c.type }))
      );
      let best = top[0];
      let bestDur = routes[0]?.durationSec ?? Infinity;
      for (let i = 1; i < top.length; i++) {
        if ((routes[i]?.durationSec ?? Infinity) < bestDur) {
          best = top[i];
          bestDur = routes[i].durationSec;
        }
      }
      handleDispatchUnit(callId, best.apparatusId);
    } catch (_e) {
      // Fallback: straight-line nearest if OSRM is unreachable.
      handleDispatchUnit(callId, candidates[0].apparatusId);
    }
  }, [handleDispatchUnit]);

  // ---------- Quick Finish (gem-bypass for an active call) ----------
  // Charges quickFinishGems(reward) server-side, then locally drives the
  // call to instant resolution by setting onSceneStartedAt deep in the past
  // and flipping it to on_scene with a `quickFinish` marker. The engine's
  // pause-check skips quickFinish'd calls, so the very next 500 ms tick
  // resolves it via the same code path as a natural resolution — paying
  // money, freeing units, firing the radio entry + onCallResolved bridge.
  const handleQuickFinish = useCallback(async (callId) => {
    const call = ref.current.calls.find((c) => c.id === callId);
    if (!call) return;
    const t = EMERGENCY_TYPES[call.type];
    const reward = t?.reward || 0;
    const cost = quickFinishGems(reward);
    if (ref.current.player.gems < cost) {
      toast.error(`Need ${cost} gems (you have ${ref.current.player.gems})`);
      return;
    }
    try {
      const updated = await quickFinishCallApi(reward);
      // Sync gems (backend deducted them). Money + XP follow when the
      // engine naturally resolves the call below.
      setPlayer((p) => ({ ...p, gems: updated.gems }));
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not finish call");
      return;
    }
    // Force-resolve: mark the call as on_scene with a fully-elapsed timer
    // and a quickFinish flag that skips the engine's "requirements drop
    // → pause" guard.
    setCalls((prev) =>
      prev.map((c) =>
        c.id === callId
          ? {
              ...c,
              revealed: true,
              state: "on_scene",
              quickFinish: true,
              onSceneStartedAt: Date.now() - (c.onSceneDurationSec * 1000) - 1000,
            }
          : c
      )
    );
    pushRadio("resolve", `${t?.label || "Call"} resolved via emergency bypass (gems)`);
  }, [pushRadio]);

  // ---------- Mutual Aid (extracted to useMutualAid hook) ----------
  const mutualAid = useMutualAid({
    user,
    stateRef: ref,
    dispatches,
    setDispatches,
    setApparatusState,
    calls,
    setCalls,
    pushRadio,
    fireGameEvent,
    scheduleStaggered,
    nextDispatchAtRef,
  });
  const {
    alliance,
    shares,
    allianceStations,
    myShares,
    sharedCallIds,
    activeShare,
    mutualAidOpen,
    helpPanelOpen,
    showAllianceStations,
    showAllianceUnits,
    dispatchesWithHelpers,
    refreshAlliance,
    openShare: handleOpenShare,
    closeShareView,
    setMutualAidOpen,
    toggleHelpPanel,
    toggleAllianceStations,
    toggleAllianceUnits,
    handleShareCall,
    handleCloseShare,
    sendUnitToShare,
    handleRespondToShare,
    handleCallUpgraded: mutualAidCallUpgraded,
    handleCallResolved: mutualAidCallResolved,
  } = mutualAid;
  // Keep ref.current.alliance fresh — used by handleMapClick for the
  // alliance-hospital placement flow which fires synchronously in a closure.
  ref.current.alliance = alliance;

  // Wire engine bridges so useGameEngine's onCallResolved / onCallUpgraded
  // invoke the mutual-aid handlers (the engine reads via refs to avoid
  // tearing down its 500 ms / 100 ms intervals every render).
  useEffect(() => { handleCallUpgradedRef.current = mutualAidCallUpgraded; },
    [mutualAidCallUpgraded]);
  useEffect(() => { handleCallResolvedRef.current = mutualAidCallResolved; },
    [mutualAidCallResolved]);

  // ---------- Transport pick (manual + auto-transport) ----------
  // Shared logic between TransportDialog's onPick and the auto-transport
  // effect. Performs all 3 cases (helper-on-share, host-using-helper-medic,
  // host-on-own-call) so callers don't need to think about them.
  //
  // `transportClaimsRef` prevents a race when the auto-transport effect fires
  // performTransport in a tight loop with stale `dispatches`. Without the
  // claim, two concurrent calls would both `.find()` the same on-scene
  // Medic/HEMS and assign different patients to it — only the last
  // `setDispatches` would stick and the others would silently become
  // un-attached "in_transit" patients. Claims live for the lifetime of one
  // performTransport call; they're cleared on success, failure, or error.
  const transportClaimsRef = useRef(new Set());

  const performTransport = useCallback(async ({ patient, call, hospital: h, share }) => {
    if (!patient || !call || !h) return;
    // ---- Resolve which transport unit (Medic OR HEMS) to use ----
    // Patients flagged `requires_hems: true` (critical, post-assessment) can
    // ONLY be transported by HEMS — Medic ambulances cannot take them.
    const needsHemsOnly = !!patient.requires_hems;
    const isTransportType = (t) => (needsHemsOnly ? t === "HEMS" : (t === "Medic" || t === "HEMS"));
    const isAvailable = (x) => x.onScene && !x.transporting && !x.returning && isTransportType(x.apparatusType);
    let d;
    if (share) {
      d = dispatches.find(
        (x) => x.sharedId === share.id && isAvailable(x) && !transportClaimsRef.current.has(x.id)
      );
      if (!d) {
        toast.error(needsHemsOnly ? "Patient is critical — only a HEMS helicopter can transport. None of yours are free on scene." : "No free Medic/HEMS of yours on scene");
        return;
      }
    } else {
      d = dispatchesWithHelpers.find(
        (x) => x.callId === call.id && isAvailable(x) && !transportClaimsRef.current.has(x.id)
      );
      if (!d) {
        toast.error(needsHemsOnly ? "Patient is critical — only a HEMS helicopter can transport. None on scene." : "No free Medic or HEMS on scene");
        return;
      }
    }
    // CLAIM the carrier synchronously so the next concurrent call to
    // performTransport (likely from the auto-transport loop firing on the
    // same stale `dispatches` snapshot) skips this unit.
    transportClaimsRef.current.add(d.id);
    const releaseClaim = () => transportClaimsRef.current.delete(d.id);
    const isExternalHelperMedic = !!d.external;
    const isHelperOwnLocal = !!share;

    // Resolve which share applies (for offline-safe server ETA timer).
    let activeShareForCall = share || null;
    if (!activeShareForCall && !isExternalHelperMedic) {
      activeShareForCall = shares.find(
        (s) => s.requester_id === user?.user_id && s.call?.id === call.id
      ) || null;
    }
    if (!activeShareForCall && isExternalHelperMedic) {
      activeShareForCall = shares.find((s) => s.call?.id === call.id) || null;
    }

    // Route call → hospital (used for ETA + local animation).
    let route;
    try {
      route = await getRoute({ lat: call.lat, lng: call.lng }, { lat: h.lat, lng: h.lng }, { apparatusType: d.apparatusType });
    } catch (_e) {
      route = { coords: [[call.lat, call.lng], [h.lat, h.lng]], durationSec: 60 };
    }
    const etaSec = Math.max(15, Math.round(route.durationSec) + 12);

    if (!isExternalHelperMedic) {
      const aState = apparatusState[d.apparatusId];
      let home = null;
      if (aState?.stationId) {
        const s = ref.current.stations.find((x) => x.id === aState.stationId);
        if (s) home = { lat: s.lat, lng: s.lng, kind: "station", id: s.id };
      } else if (aState?.hospitalId) {
        const hh = ref.current.hospitals.find((x) => x.id === aState.hospitalId);
        if (hh) home = { lat: hh.lat, lng: hh.lng, kind: "hospital", id: hh.id };
      }
      if (!home) { toast.error("Medic has no home base"); releaseClaim(); return; }

      let reservation;
      try {
        reservation = await reserveBed(
          h.id,
          { patient_id: patient.id, apparatus_id: d.apparatusId, specialty: patient.specialty || null },
          h.kind === "player_shared" ? h.owner_user_id : undefined,
          h.kind === "alliance" ? h.alliance_id : undefined,
        );
      } catch (e) {
        toast.error(e?.response?.data?.detail || "No beds available");
        releaseClaim();
        return;
      }

      setDispatches((ds) => ds.map((x) => x.id === d.id ? {
        ...x,
        transporting: true,
        onScene: false,
        returning: false,
        queued: false,
        routeCoords: route.coords,
        lat: route.coords[0][0],
        lng: route.coords[0][1],
        toLat: h.lat,
        toLng: h.lng,
        startedAt: Date.now(),
        durationSec: route.durationSec,
        outboundDurationSec: route.durationSec,
        transportPatientId: patient.id,
        transportSpecialty: patient.specialty || null,
        transportHospitalId: h.id,
        transportHospitalName: h.name,
        transportHospitalOwnerId: h.kind === "player_shared" ? h.owner_user_id : null,
        transportHospitalAllianceId: h.kind === "alliance" ? h.alliance_id : null,
        transportReservationId: reservation.reservation_id,
        transportHomeLat: home.lat,
        transportHomeLng: home.lng,
        transportHomeId: home.id,
        transportHomeKind: home.kind,
      } : x));

      if (!isHelperOwnLocal) {
        setCalls((prev) => prev.map((c) => c.id !== call.id ? c : {
          ...c,
          patients: (c.patients || []).map((p) =>
            p.id === patient.id ? { ...p, transportStatus: "in_transit", destinationHospitalId: h.id } : p
          ),
        }));
      }
      pushRadio("dispatch", `${d.apparatusName} transporting patient to ${h.name}`);
    } else {
      pushRadio("dispatch", `${d.apparatusName} transporting patient to ${h.name} (alliance)`);
      setCalls((prev) => prev.map((c) => c.id !== call.id ? c : {
        ...c,
        patients: (c.patients || []).map((p) =>
          p.id === patient.id ? { ...p, transportStatus: "in_transit", destinationHospitalId: h.id } : p
        ),
      }));
    }

    if (activeShareForCall && (isExternalHelperMedic || isHelperOwnLocal)) {
      const reward = patientReward(patient, h);
      try {
        await sharePatientTransport(alliance.id, activeShareForCall.id, {
          patient_id: patient.id,
          hospital_id: h.id,
          hospital_name: h.name || "",
          hospital_lat: h.lat,
          hospital_lng: h.lng,
          hospital_owner_id: h.kind === "player_shared" ? h.owner_user_id : null,
          hospital_alliance_id: h.kind === "alliance" ? h.alliance_id : null,
          specialty: patient.specialty || null,
          eta_sec: etaSec,
          reward,
          medic_user_id: isHelperOwnLocal ? user?.user_id : null,
          medic_dispatch_id: typeof d.id === "number" ? d.id : null,
        });
      } catch (_e) { /* best-effort */ }
    }
    // Release claim AFTER state mutations have been queued. The dispatch
    // now has `transporting: true` so the next render's availability filter
    // will naturally exclude it from future picks. We delay one tick to
    // ensure both setDispatches calls flushed in the current commit before
    // a tight follow-up performTransport call could see the carrier as
    // "free" again.
    queueMicrotask(releaseClaim);
  }, [dispatches, dispatchesWithHelpers, shares, alliance, user, apparatusState, ref, setDispatches, setCalls, pushRadio]);

  // ---------- Auto Transport (premium feature) ----------
  // When the player has Auto Transport ON, watch local calls for patients
  // that just became `assessed && needsTransport && transportStatus === "none"`,
  // pick a destination hospital per the chosen mode, and fire performTransport.
  // Per-patient guard so we never auto-fire twice for the same patient.
  const autoFiredRef = useRef(new Set());
  useEffect(() => {
    if (!preferences.auto_transport_enabled || !isPremium) return;
    const mode = preferences.auto_transport_mode === "capable" ? "capable" : "closest";
    let cancelled = false;
    (async () => {
      // ----- Self-heal stale autoFiredRef entries -----
      // The ref guards against double-firing for the same patient, but it
      // can keep a stale entry from a previous attempt where:
      //   - the candidate was generated then dropped by `freeCarriers` cap,
      //   - performTransport raced and aborted, OR
      //   - the patient got manually transported and then somehow reverted.
      // If a patient is back to `status="none"` (e.g. a WAITING patient
      // whose first carrier never showed up), we must clear the guard so a
      // newly-arriving Medic / HEMS can pick them up. Same for share
      // patients keyed by `share:{share_id}:{patient_id}`.
      const liveStatusById = new Map();
      for (const c of calls) {
        for (const p of c.patients || []) {
          liveStatusById.set(p.id, p.transportStatus || "none");
        }
      }
      for (const s of shares || []) {
        for (const p of s.patients || []) {
          liveStatusById.set(`share:${s.id}:${p.id}`, p.transportStatus || "none");
        }
      }
      const isInflight = (st) => st === "in_transit" || st === "queued";
      for (const key of Array.from(autoFiredRef.current)) {
        const st = liveStatusById.get(key);
        if (!st || (!isInflight(st) && st !== "delivered")) {
          // Patient either no longer present in any visible call OR back to
          // "none" / "refused" / "cancelled" — clear the guard so the
          // candidate generation below can re-fire.
          autoFiredRef.current.delete(key);
        }
      }

      // Find candidate patients: assessed, needsTransport, transportStatus=none.
      // Only consider calls where I have a free Medic on scene (so the
      // transport will actually succeed). External helper-medic auto-pick is
      // intentionally NOT handled — that's the helper's choice.
      const candidates = [];
      for (const c of calls) {
        if (!Array.isArray(c.patients) || c.patients.length === 0) continue;
        // Free transport carriers for THIS call, split by type. HEMS-only
        // patients (requires_hems) can ONLY use HEMS; Medic patients can
        // use either. So we track each pool's slots independently.
        const onSceneMedHems = dispatchesWithHelpers.filter(
          (d) => d.callId === c.id && d.onScene && !d.returning && !d.transporting && (d.apparatusType === "Medic" || d.apparatusType === "HEMS") && !d.external
        );
        let freeHems = onSceneMedHems.filter((d) => d.apparatusType === "HEMS").length;
        let freeMedic = onSceneMedHems.filter((d) => d.apparatusType === "Medic").length;
        if (freeHems + freeMedic === 0) continue;
        for (const p of c.patients) {
          if (!p.assessed) continue;
          if (!p.needsTransport) continue;
          if ((p.transportStatus || "none") !== "none") continue;
          if (autoFiredRef.current.has(p.id)) continue;
          const requiresHems = !!p.requires_hems;
          if (requiresHems) {
            if (freeHems <= 0) continue;  // skip — needs HEMS specifically
            freeHems -= 1;
          } else {
            // Prefer Medic for non-critical patients so HEMS stays free
            // for any HEMS-only patient further down the list.
            if (freeMedic > 0) freeMedic -= 1;
            else if (freeHems > 0) freeHems -= 1;
            else continue;
          }
          candidates.push({ call: c, patient: p });
        }
      }
      // ---- ALSO auto-transport on alliance shares where I helped ----
      // For shared calls (I'm a helper, not the host), iterate `shares` and
      // surface patients that are assessed + needsTransport + still `none`.
      // Only fire when MY Medic is on scene of that share (`dispatches` with
      // sharedId + onScene + apparatusType "Medic"). This matches what
      // `performTransport({ share })` will then use as the transport vehicle.
      for (const s of shares) {
        if (!s || s.requester_id === user?.user_id) continue; // I'm host, not helper
        const myFreeCarriers = dispatches.filter(
          (d) => d.sharedId === s.id && d.onScene && !d.returning && !d.transporting && (d.apparatusType === "Medic" || d.apparatusType === "HEMS")
        ).length;
        if (myFreeCarriers === 0) continue;
        const sharedCall = s.call || {};
        const ptList = Array.isArray(s.patients) ? s.patients : [];
        let assignedShare = 0;
        for (const p of ptList) {
          if (!p.assessed) continue;
          if (!p.needsTransport) continue;
          if ((p.transportStatus || "none") !== "none") continue;
          const fireKey = `share:${s.id}:${p.id}`;
          if (autoFiredRef.current.has(fireKey)) continue;
          if (assignedShare >= myFreeCarriers) break;  // out of carriers
          candidates.push({
            call: {
              id: sharedCall.id,
              lat: sharedCall.lat,
              lng: sharedCall.lng,
              type: sharedCall.type,
              address: sharedCall.address,
            },
            patient: p,
            share: s,
            _fireKey: fireKey,
          });
          assignedShare += 1;
        }
      }
      if (candidates.length === 0) return;

      // Fetch hospital list once for this batch (free beds + specialties).
      let hospitalList = [];
      try {
        const d = await listEligibleHospitals();
        hospitalList = d.hospitals || [];
      } catch (_e) {
        return;
      }
      if (cancelled) return;

      // Mark fired immediately to prevent re-entry on the next render. We
      // mark BEFORE awaits because performTransport itself will flip the
      // patient's status to in_transit which would also block re-firing,
      // but a fast re-render before the awaits resolve could otherwise
      // trigger a duplicate.
      candidates.forEach((c) => autoFiredRef.current.add(c._fireKey || c.patient.id));

      for (const { call, patient, share, _fireKey } of candidates) {
        const annotated = hospitalList
          .map((h) => {
            const dist = distanceKm({ lat: h.lat, lng: h.lng }, { lat: call.lat, lng: call.lng });
            const specMatch = patient.specialty && (h.specialties || []).includes(patient.specialty);
            return { ...h, dist, specMatch, freeBeds: h.free_beds };
          })
          .filter((h) => (h.freeBeds ?? 0) > 0);
        if (annotated.length === 0) {
          // No beds anywhere — clear the guard so we can retry later.
          autoFiredRef.current.delete(_fireKey || patient.id);
          continue;
        }
        let pick;
        if (mode === "capable" && patient.specialty) {
          // Prefer specialty match (nearest such hospital). Fall back to closest.
          const matches = annotated.filter((h) => h.specMatch).sort((a, b) => a.dist - b.dist);
          pick = matches[0] || [...annotated].sort((a, b) => a.dist - b.dist)[0];
        } else {
          pick = [...annotated].sort((a, b) => a.dist - b.dist)[0];
        }
        if (!pick) continue;
        // Fire-and-forget — performTransport already handles errors with toasts.
        // Awaiting in-series keeps bed-reservation contention sane.
        try {
          await performTransport({ patient, call, hospital: pick, share });
        } catch (_e) {
          autoFiredRef.current.delete(_fireKey || patient.id);
        }
      }
    })();
    return () => { cancelled = true; };
  }, [calls, dispatches, dispatchesWithHelpers, shares, user?.user_id, preferences.auto_transport_enabled, preferences.auto_transport_mode, isPremium, performTransport]);

  // ---------- Auto-Clear Medics (premium) ----------
  // When every patient on an EMS call has been either transported (in_transit
  // / queued / delivered) or refused, any Medic/HEMS still standing on scene
  // has nothing left to do. Auto-clear them via the same recall path the
  // dispatcher would use manually, so the player doesn't have to click
  // Cancel for each straggler.
  //
  // Guarded by:
  //   • `auto_clear_medics_enabled` + `isPremium` (feature gate)
  //   • `autoClearedRef` dispatchId dedup — the tick fires every 500 ms so
  //     without this a single Medic would try to recall itself several times
  //     before the recall's staggered timer actually kicks in.
  const autoClearedRef = useRef(new Set());
  useEffect(() => {
    if (!preferences.auto_clear_medics_enabled || !isPremium) return;
    const patientsAllSettled = (c) => {
      if (!Array.isArray(c.patients) || c.patients.length === 0) return false;
      return c.patients.every(
        (p) =>
          p.assessed &&
          (!p.needsTransport ||
            p.transportStatus === "in_transit" ||
            p.transportStatus === "queued" ||
            p.transportStatus === "delivered" ||
            p.transportStatus === "refused")
      );
    };
    const settledCallIds = new Set(
      calls.filter(patientsAllSettled).map((c) => c.id)
    );
    if (settledCallIds.size === 0) return;
    dispatches.forEach((d) => {
      if (d.external) return; // ally helpers manage their own units
      if (!d.onScene) return;
      if (d.returning) return;
      if (d.transporting) return; // busy taking a patient in — leave it
      if (d.unloading) return;    // dropping off at the hospital
      if (d.apparatusType !== "Medic" && d.apparatusType !== "HEMS") return;
      if (!settledCallIds.has(d.callId)) return;
      if (autoClearedRef.current.has(d.id)) return;
      autoClearedRef.current.add(d.id);
      handleCancelDispatch(d.id);
      pushRadio("recall", `${d.apparatusName} — patients cleared, auto-recalled.`);
    });
    // Housekeeping: purge dedup entries for dispatches no longer on scene
    // (already recalled, resolved, etc.) so we don't leak indefinitely.
    const liveIds = new Set(dispatches.map((d) => d.id));
    autoClearedRef.current.forEach((id) => {
      if (!liveIds.has(id)) autoClearedRef.current.delete(id);
    });
  }, [calls, dispatches, preferences.auto_clear_medics_enabled, isPremium, handleCancelDispatch, pushRadio]);

  // Keep `allianceHospitals` state in sync with the latest alliance object so
  // the MapView and BuildFacility dropdown both see alliance-owned hospitals.
  // The `apiMyAlliance` endpoint only returns hospital_count for compactness,
  // so we hit `/api/alliances/{aid}/hospitals` whenever the alliance id changes
  // (and any time `refreshAlliance` runs, the alliance object reference changes).
  useEffect(() => {
    if (!alliance?.id) {
      setAllianceHospitals([]);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const d = await listAllianceHospitals(alliance.id);
        if (!cancelled) setAllianceHospitals(d.alliance || []);
      } catch (_e) {
        if (!cancelled) setAllianceHospitals([]);
      }
    })();
    return () => { cancelled = true; };
  }, [alliance]);

  // ---------- User menu actions ----------
  const [profileOpen, setProfileOpen] = useState(false);
  const [buyGemsOpen, setBuyGemsOpen] = useState(false);
  const [achievementsOpen, setAchievementsOpen] = useState(false);
  const [dailyOpen, setDailyOpen] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [developerOpen, setDeveloperOpen] = useState(false);
  const [faqHelpOpen, setFaqHelpOpen] = useState(false);
  const [friendsOpen, setFriendsOpen] = useState(false);
  const [newsOpen, setNewsOpen] = useState(false);
  const [newsUnreadCount, setNewsUnreadCount] = useState(0);
  const [gameVersion, setGameVersion] = useState("");
  // ---------- Mobile / small-screen state ----------
  // On phones + tablets (<1024px) the right-side Mutual Aid panel, the
  // bottom Fleet/Radio strip, and the help-requests rail are all hidden.
  // Each gets a floating action button that opens it as a full-screen
  // drawer. Desktop layout is untouched.
  const isSmallScreen = useIsTabletOrBelow();
  const [mobileFleetOpen, setMobileFleetOpen] = useState(false);
  const [mobileRadioOpen, setMobileRadioOpen] = useState(false);
  const [mobileAidOpen, setMobileAidOpen] = useState(false);
  const [dailyStatus, setDailyStatus] = useState(null);
  const handleOpenProfile = useCallback(() => setProfileOpen(true), []);
  const handleOpenBuyGems = useCallback(() => setBuyGemsOpen(true), []);
  const handleOpenAchievements = useCallback(() => setAchievementsOpen(true), []);
  const handleOpenDailyBonus = useCallback(() => setDailyOpen(true), []);
  const handleOpenSettings = useCallback(() => setSettingsOpen(true), []);
  const handleOpenDeveloperPanel = useCallback(() => setDeveloperOpen(true), []);
  const handleOpenFaqHelp = useCallback(() => setFaqHelpOpen(true), []);
  const handleOpenFriends = useCallback(() => setFriendsOpen(true), []);
  const handleOpenNews = useCallback(() => setNewsOpen(true), []);
  // Called by NewsDialog after it POSTs /news/mark-seen. Zeroes the badge
  // instantly so the UI feels responsive; the next poll would clear it
  // anyway but the delay would be visible.
  const handleNewsSeen = useCallback(() => setNewsUnreadCount(0), []);

  // Fetch the game version once + poll unread news every 60 s. Both are
  // cheap endpoints, and 60 s is plenty for a "new post available" cue
  // without hammering the backend.
  useEffect(() => {
    if (!loaded) return;
    let cancelled = false;
    const refresh = async () => {
      try {
        const [v, u] = await Promise.all([getGameVersion(), getNewsUnread()]);
        if (!cancelled) {
          setGameVersion(v?.version || "");
          setNewsUnreadCount(u?.count || 0);
        }
      } catch (_e) {
        /* silent — badge just stays at last known value */
      }
    };
    refresh();
    const t = setInterval(refresh, 60_000);
    return () => { cancelled = true; clearInterval(t); };
  }, [loaded]);

  // Friend-request + unread-DM counts for HUD badges. Polled lazily; quiets
  // itself while the FriendsDialog is open (that dialog has its own poller).
  const friendNotifications = useFriendNotifications({ enabled: loaded && !friendsOpen });

  // Auto-open FAQ / Help on a user's FIRST visit on a device. Stored in
  // localStorage so it never reopens unprompted; user can always reopen
  // from the user dropdown ("FAQ / Help" menu entry).
  useEffect(() => {
    if (!loaded) return;
    const KEY = "dispatch911_faq_seen";
    if (localStorage.getItem(KEY)) return;
    const t = setTimeout(() => {
      setFaqHelpOpen(true);
      localStorage.setItem(KEY, String(Date.now()));
    }, 1200);
    return () => clearTimeout(t);
  }, [loaded]);
  // Developer role detected from auth user. Used to conditionally show

  // ---- Follow-Up queue: load, persist, and auto-dispatch ----
  // Storage key is user-scoped so switching accounts on the same browser
  // doesn't cross-contaminate queues. localStorage only — the follow-up
  // queue is intentionally session-local; if you sign in on another device
  // any queued responders reset.
  const followUpStorageKey = user?.user_id ? `dispatch911_followup_${user.user_id}` : null;
  // State-based hydration flag: persist effect must wait until the load has
  // COMMITTED (setFollowUpQueue queued in the same batch as setFollowUpHydrated).
  // A ref would flip synchronously and let the persist effect run in the same
  // effect cycle with the stale `{}` closure — clobbering localStorage before
  // the loaded value ever hits state (this bug is amplified under React
  // StrictMode's double-invocation).
  const [followUpHydrated, setFollowUpHydrated] = useState(false);
  useEffect(() => {
    if (!followUpStorageKey) { setFollowUpHydrated(false); return; }
    setFollowUpHydrated(false);
    try {
      const raw = localStorage.getItem(followUpStorageKey);
      const parsed = raw ? (JSON.parse(raw) || {}) : {};
      setFollowUpQueue(parsed);
    } catch { /* corrupt payload — ignore */ }
    setFollowUpHydrated(true);
  }, [followUpStorageKey]);
  useEffect(() => {
    if (!followUpStorageKey) return;
    if (!followUpHydrated) return; // never overwrite pre-load
    try { localStorage.setItem(followUpStorageKey, JSON.stringify(followUpQueue)); } catch { /* quota full — non-fatal */ }
  }, [followUpQueue, followUpStorageKey, followUpHydrated]);
  // Keep a ref shadowing the follow-up queue so the engine's synchronous
  // `handleCallResolved` bridge can read the latest queue value without
  // being reconstructed each render. The state variant drives the UI; the
  // ref drives the auto-dispatch logic below.
  const followUpQueueRef = useRef({});
  useEffect(() => { followUpQueueRef.current = followUpQueue; }, [followUpQueue]);

  // Public toggle used by CallDialog: adds or removes an apparatus from
  // the follow-up queue for a given call. Only one call at a time can
  // "own" a queued unit — assigning it to a new call moves it off any
  // prior queue.
  const toggleFollowUp = useCallback((callId, apparatusId) => {
    // Normalize the target call id to a string once so all downstream
    // comparisons against `Object.entries(q)` (which yields string keys)
    // are consistent. Without this, `cid === callId` compares a string to
    // a number and same-call cancellation never triggers.
    const targetKey = String(callId);
    setFollowUpQueue((q) => {
      const nx = {};
      let alreadyQueuedForThis = false;
      Object.entries(q).forEach(([cid, ids]) => {
        const kept = (ids || []).filter((aid) => {
          if (aid !== apparatusId) return true;
          if (String(cid) === targetKey) { alreadyQueuedForThis = true; return false; }
          return false; // strip from other calls
        });
        if (kept.length) nx[cid] = kept;
      });
      if (!alreadyQueuedForThis) {
        nx[targetKey] = [...(nx[targetKey] || []), apparatusId];
      }
      return nx;
    });
  }, []);
  // Purge queue entries for calls that no longer exist (resolved / expired
  // OR target-call resolved). This also acts as a safety net alongside the
  // handleCallResolved bridge below.
  //
  // NB: `followUpQueue` is a plain object → its keys are always STRINGS,
  // but call.id is a NUMBER from `nextCallId()`. Normalize both to string
  // before comparing or `Set.has(stringKey)` returns false for numeric
  // members and this effect wipes the entire queue every time `calls`
  // changes (which is every ~500 ms during normal play).
  //
  // HYDRATION GUARD: On initial mount/reload, `calls` is briefly `[]` before
  // the server responds. Without this guard the sweep runs against an empty
  // live-set, wipes every queued entry, and the persist effect flushes the
  // empty state back to localStorage — permanently losing user queues. Wait
  // until `loaded === true` (successful server hydration) before sweeping.
  useEffect(() => {
    if (!loaded) return;
    const liveIds = new Set(calls.map((c) => String(c.id)));
    setFollowUpQueue((q) => {
      let changed = false;
      const nx = {};
      Object.entries(q).forEach(([cid, ids]) => {
        if (liveIds.has(String(cid))) nx[cid] = ids;
        else changed = true;
      });
      return changed ? nx : q;
    });
  }, [calls, loaded]);

  // Follow-Up bridge: wire the resolve callback now that
  // `handleDispatchUnit` + `followUpQueueRef` exist. Runs once per engine
  // resolve tick. Reads which units WERE on-scene of the resolving calls
  // via `ref.current.dispatches` (still snapshots the pre-resolve state
  // because engine hasn't committed the new setDispatches yet — perfect
  // for finding who just cleared). For each on-scene unit that's queued
  // for a still-active target call, immediately calls handleDispatchUnit.
  useEffect(() => {
    followUpOnResolvedRef.current = (resolvedCalls) => {
      if (!Array.isArray(resolvedCalls) || resolvedCalls.length === 0) return;
      const resolvedIds = new Set(resolvedCalls.map((c) => c?.id).filter(Boolean));
      if (resolvedIds.size === 0) return;
      // Units that were on scene of a resolving call — these are the ones
      // about to become available.
      const freedUnitIds = ref.current.dispatches
        .filter((d) => d.callId && resolvedIds.has(d.callId) && d.onScene)
        .map((d) => d.apparatusId);
      if (freedUnitIds.length === 0) return;
      const q = followUpQueueRef.current || {};
      // targetByApp: apparatusId -> callId (first queued match wins)
      const targetByApp = {};
      Object.entries(q).forEach(([cid, ids]) => {
        (ids || []).forEach((aid) => { if (!(aid in targetByApp)) targetByApp[aid] = cid; });
      });
      const toFire = [];
      const toPurge = [];
      // `followUpQueue` keys are STRINGS (object semantics); `calls[i].id`
      // is a NUMBER from `nextCallId()`. Normalize both sides to string
      // before Set membership checks — otherwise `has()` returns false
      // for perfectly-live target calls.
      const liveCallIds = new Set(ref.current.calls.map((c) => String(c.id)));
      resolvedIds.forEach((id) => liveCallIds.delete(String(id)));
      freedUnitIds.forEach((aid) => {
        const targetCallId = targetByApp[aid];
        if (targetCallId == null) return;
        if (liveCallIds.has(String(targetCallId))) toFire.push({ aid, targetCallId });
        else toPurge.push(aid);
      });
      if (toFire.length === 0 && toPurge.length === 0) return;
      // Fire dispatches on the next tick so React can commit the resolve
      // first (this ensures apparatusState[aid].available is true when
      // handleDispatchUnit checks it).
      if (toFire.length) {
        setTimeout(() => {
          toFire.forEach(({ aid, targetCallId }) => {
            // targetCallId is a STRING (object key); apparatus + call
            // lookups elsewhere use strict-eq against NUMBER ids. Coerce
            // for both the `still-live` check and the actual dispatch call.
            const still = ref.current.calls.some((c) => String(c.id) === String(targetCallId));
            const aState = ref.current.apparatusState[aid];
            if (!still || !aState || !aState.available) return;
            const numericCallId = Number(targetCallId);
            handleDispatchUnit(Number.isFinite(numericCallId) ? numericCallId : targetCallId, aid);
            pushRadio("dispatch", `${aState.name} cleared previous call — Follow-Up auto-dispatched.`);
          });
        }, 60);
      }
      // Strip fired + stale entries. Never touch entries for units NOT in
      // the resolved-call set — that was the source of the original bug.
      setFollowUpQueue((cur) => {
        const nx = {};
        const firedIds = new Set(toFire.map((t) => t.aid));
        const purgeIds = new Set(toPurge);
        Object.entries(cur).forEach(([cid, ids]) => {
          const kept = (ids || []).filter((aid) => !firedIds.has(aid) && !purgeIds.has(aid));
          if (kept.length) nx[cid] = kept;
        });
        return nx;
      });
    };
  }, [handleDispatchUnit, pushRadio]);

  const isDeveloper = !!user?.roles?.includes?.("developer");
  const handleGemsChanged = useCallback(({ money, gems }) => {
    setPlayer((p) => ({
      ...p,
      money: typeof money === "number" ? money : p.money,
      gems: typeof gems === "number" ? gems : p.gems,
    }));
  }, []);

  // ---------- Daily bonus: fetch status on mount; auto-open dialog if claimable ----------
  useEffect(() => {
    let alive = true;
    getDailyStatus()
      .then((s) => {
        if (!alive) return;
        setDailyStatus(s);
        if (s.claimable) {
          // Wait a beat so the user lands in the game first before the modal pops.
          setTimeout(() => alive && setDailyOpen(true), 800);
        }
      })
      .catch(() => {});
    return () => { alive = false; };
  }, []);

  const handleDailyClaimed = useCallback(({ gems_awarded, streak_day, current_gems }) => {
    setPlayer((p) => ({ ...p, gems: current_gems }));
    setDailyStatus((s) => s ? {
      ...s,
      claimable: false,
      current_streak: streak_day,
      // Best-effort: clear the "claimable" flag; real status will refresh next session
    } : s);
    pushRadio("daily", `Daily bonus claimed (+${gems_awarded} gems, day ${streak_day})`);
  }, [pushRadio]);

  // ---------- Handle Stripe success redirect ----------
  // When returning from Stripe Checkout, URL contains ?gems_session_id=...
  // Poll the backend for status, credit gems once confirmed, then clean the URL.
  useEffect(() => {
    const url = new URL(window.location.href);
    const sid = url.searchParams.get("gems_session_id");
    if (!sid) return;
    let attempts = 0;
    const MAX = 8;
    let cancelled = false;
    let timer;
    const poll = async () => {
      if (cancelled) return;
      attempts += 1;
      try {
        const r = await getGemCheckoutStatus(sid);
        if (r.payment_status === "paid") {
          handleGemsChanged({ money: r.current_money, gems: r.current_gems });
          pushRadio(
            "daily",
            `Payment complete — +${r.gems_credited.toLocaleString()} gems credited to account`
          );
          cleanUrl();
          return;
        }
        if (r.status === "expired" || r.status === "canceled") {
          pushRadio("expired", "Gem purchase was canceled or expired.");
          cleanUrl();
          return;
        }
      } catch (e) {
        // 404 / 403 → stop polling
        if (e?.response?.status === 404 || e?.response?.status === 403) {
          cleanUrl();
          return;
        }
      }
      if (attempts >= MAX) {
        pushRadio("daily", "Payment still processing — gems will appear shortly.");
        cleanUrl();
        return;
      }
      timer = setTimeout(poll, 2000);
    };
    const cleanUrl = () => {
      url.searchParams.delete("gems_session_id");
      window.history.replaceState({}, "", url.pathname + (url.search || "") + url.hash);
    };
    poll();
    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ---------- Station dialog ----------
  const handleStationClick = useCallback((s) => {
    setDialogStation(s);
    setStationDialogOpen(true);
  }, []);

  const handlePlayerUpdate = useCallback((updatedPlayer) => {
    if (Array.isArray(updatedPlayer.stations)) {
      setStations(updatedPlayer.stations);
    }
    if (Array.isArray(updatedPlayer.hospitals)) {
      setHospitals(updatedPlayer.hospitals);
    }
    setPlayer((p) => ({
      ...p,
      money: typeof updatedPlayer.money === "number" ? updatedPlayer.money : p.money,
      gems: typeof updatedPlayer.gems === "number" ? updatedPlayer.gems : p.gems,
    }));
    setApparatusState((prev) => {
      const next = { ...prev };
      (updatedPlayer.stations || []).forEach((s) => {
        (s.trucks || []).forEach((t) => {
          next[t.id] = {
            stationId: s.id,
            name: t.name,
            type: t.type || "Engine",
            available: prev[t.id]?.available ?? true,
          };
        });
      });
      // Hospitals also host Medic units — register them in apparatusState so
      // they're dispatchable like any other apparatus.
      (updatedPlayer.hospitals || []).forEach((h) => {
        (h.trucks || []).forEach((t) => {
          next[t.id] = {
            hospitalId: h.id,
            stationId: null,
            name: t.name,
            type: t.type || "Medic",
            available: prev[t.id]?.available ?? true,
          };
        });
      });
      return next;
    });
    setDialogStation((cur) =>
      cur ? (updatedPlayer.stations || []).find((s) => s.id === cur.id) || cur : cur
    );
    setDialogHospital((cur) =>
      cur ? (updatedPlayer.hospitals || []).find((h) => h.id === cur.id) || cur : cur
    );
  }, []);

  // ---------- Call dialog ----------
  const handleCallClick = useCallback((c) => {
    setActiveCall(c);
    setCallDialogOpen(true);
  }, []);
  // Keep the open call's data fresh as state changes
  useEffect(() => {
    if (!activeCall) return;
    const fresh = calls.find((c) => c.id === activeCall.id);
    if (fresh && fresh !== activeCall) setActiveCall(fresh);
    if (!fresh) setCallDialogOpen(false);
  }, [calls, activeCall]);

  // ---------- Sound toggle ----------
  const handleSoundToggle = useCallback(() => {
    setSoundOn((v) => {
      const nv = !v;
      setSoundEnabled(nv);
      return nv;
    });
  }, []);

  // Dev/test hook so you can drive the simulation without waiting 90s
  useEffect(() => {
    window.__dispatch911 = {
      spawnCall: () => spawnCall(ref.current.stations),
      dispatchUnit: handleDispatchUnit,
      getCalls: () => ref.current.calls,
      getDispatches: () => ref.current.dispatches,
      getApparatusState: () => ref.current.apparatusState,
    };
  }, [spawnCall, handleDispatchUnit]);

  const mainStation =
    stations.find((s) => s.id === mainStationId) || stations[0] || null;

  // Block the entire game UI until we have a confirmed-good state load.
  // Showing the game with default $50k while the load is still in flight
  // (or has failed) would let the snapshot heartbeat overwrite the
  // player's real balance in MongoDB.
  if (!loaded) {
    return (
      <div
        data-testid="game-loading-screen"
        className="h-screen w-screen flex items-center justify-center bg-[#050505] text-white"
      >
        <div className="text-center space-y-4 max-w-sm px-6">
          <div className="mono uppercase tracking-widest text-[11px] text-zinc-500">
            Dispatch 911
          </div>
          {loadError ? (
            <>
              <div className="text-red-400 mono uppercase tracking-widest text-[12px]">
                Could not load saved game
              </div>
              <div className="text-zinc-400 text-sm leading-relaxed">
                Your progress is safe in our database — we just couldn't reach
                it right now. Tap retry to try again.
              </div>
              <button
                data-testid="game-load-retry"
                type="button"
                onClick={() => { setLoadError(null); setLoadRetry((n) => n + 1); }}
                className="mono uppercase tracking-widest text-[11px] border border-red-500/40 bg-red-500/10 hover:bg-red-500/20 text-red-200 px-4 py-2"
              >
                Retry
              </button>
              <div className="mono text-[9px] uppercase tracking-widest text-zinc-700 break-words">
                {String(loadError).slice(0, 200)}
              </div>
            </>
          ) : (
            <div className="text-zinc-300 mono uppercase tracking-widest text-[11px] animate-pulse">
              Connecting to dispatch…
            </div>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="h-screen w-screen relative overflow-hidden bg-[#050505] text-white">
      <HUD
        player={player}
        activeCalls={calls.length}
        onLogout={onLogout}
        onOpenProfile={handleOpenProfile}
        onOpenBuyPremium={handleOpenProfile}
        onOpenSettings={handleOpenSettings}
        onOpenFaqHelp={handleOpenFaqHelp}
        onOpenFriends={handleOpenFriends}
        friendNotificationCount={friendNotifications.total}
        friendIncomingRequests={friendNotifications.incomingRequests}
        friendUnreadMessages={friendNotifications.unreadMessages}
        onOpenDeveloperPanel={handleOpenDeveloperPanel}
        isDeveloper={isDeveloper}
        onOpenNews={handleOpenNews}
        newsUnreadCount={newsUnreadCount}
        gameVersion={gameVersion}
        onOpenMutualAid={() => setMutualAidOpen(true)}
        onOpenBuyGems={handleOpenBuyGems}
        onOpenAchievements={handleOpenAchievements}
        onOpenDailyBonus={handleOpenDailyBonus}
        dailyClaimable={!!dailyStatus?.claimable}
        hasAlliance={!!alliance}
        isPremium={isPremium}
        placeMode={placeMode}
        placeModePayWith={placeModePayWith}
        placePoliceMode={placePoliceMode}
        placePolicePayWith={placePolicePayWith}
        placeHospitalMode={placeHospitalMode}
        placeHospitalPayWith={placeHospitalPayWith}
        onTogglePlace={() => {
          setPlaceMode((v) => (v && placeModePayWith === "money" ? false : true));
          setPlaceModePayWith("money");
          setPlaceHospitalMode(false);
          setPlacePoliceMode(false);
        }}
        onTogglePlaceGems={() => {
          setPlaceMode((v) => (v && placeModePayWith === "gems" ? false : true));
          setPlaceModePayWith("gems");
          setPlaceHospitalMode(false);
          setPlacePoliceMode(false);
        }}
        onTogglePlacePolice={() => {
          setPlacePoliceMode((v) => (v && placePolicePayWith === "money" ? false : true));
          setPlacePolicePayWith("money");
          setPlaceMode(false);
          setPlaceHospitalMode(false);
        }}
        onTogglePlacePoliceGems={() => {
          setPlacePoliceMode((v) => (v && placePolicePayWith === "gems" ? false : true));
          setPlacePolicePayWith("gems");
          setPlaceMode(false);
          setPlaceHospitalMode(false);
        }}
        onToggleBuildHospital={(payWith = "money") => {
          // Toggle the placement crosshair. Re-clicking the same currency
          // turns it off; clicking the other currency switches modes.
          setPlaceHospitalMode((v) => {
            if (v && (ref.current.placeHospitalPayWith || "money") === payWith) return false;
            return true;
          });
          setPlaceHospitalPayWith(payWith);
          setPlaceMode(false);
          setPlacePoliceMode(false);
        }}
        stationsCount={stations.filter((s) => (s.kind || "fire") === "fire").length}
        policeStationsCount={stations.filter((s) => (s.kind || "fire") === "police").length}
        nextStationCost={stationCost(stations.filter((s) => (s.kind || "fire") === "fire").length)}
        nextPoliceStationCost={500_000}
        nextPoliceStationGemCost={gemCost(500_000)}
        activeEvents={activeEvents}
        nextStationGemCost={gemCost(stationCost(stations.length))}
        hospitalCost={1_000_000}
        hospitalGemCost={gemCost(1_000_000)}
        soundOn={soundOn}
        onToggleSound={handleSoundToggle}
      />
      <BuyGemsDialog
        open={buyGemsOpen}
        onOpenChange={setBuyGemsOpen}
        currentGems={player.gems ?? 0}
        currentMoney={player.money ?? 0}
        onGemsChanged={handleGemsChanged}
      />
      <DailyBonusDialog
        open={dailyOpen}
        onOpenChange={setDailyOpen}
        status={dailyStatus}
        onClaimed={handleDailyClaimed}
      />
      <AchievementsDialog
        open={achievementsOpen}
        onOpenChange={setAchievementsOpen}
      />
      <SettingsDialog
        open={settingsOpen}
        onOpenChange={setSettingsOpen}
        stations={stations}
        mainStationId={mainStationId}
        onMainStationChanged={setMainStationId}
        stationZoom={stationZoom}
        onStationZoomChanged={setStationZoom}
        isPremium={isPremium}
        preferences={preferences}
        onPreferencesUpdated={(prefs) => {
          if (!prefs) return;
          setPreferences((prev) => ({ ...prev, ...prefs }));
          if (prefs?.spawn_interval_ms) setSpawnIntervalMs(prefs.spawn_interval_ms);
          if (prefs?.station_zoom) setStationZoom(prefs.station_zoom);
        }}
      />

      {isDeveloper && (
        <DeveloperDialog open={developerOpen} onOpenChange={setDeveloperOpen} />
      )}
      <FaqHelpDialog open={faqHelpOpen} onOpenChange={setFaqHelpOpen} />
      <NewsDialog
        open={newsOpen}
        onOpenChange={setNewsOpen}
        onSeen={handleNewsSeen}
      />
      <FriendsDialog
        open={friendsOpen}
        onOpenChange={(o) => {
          setFriendsOpen(o);
          // When the dialog closes, immediately refresh the HUD notification
          // count so any DMs the user just read clear the badge without
          // waiting for the next 30s poll.
          if (!o) friendNotifications.refresh();
        }}
      />
      <ProfileDialog
        open={profileOpen}
        onOpenChange={setProfileOpen}
        onUserUpdated={(u) => {
          if (u?.name) setPlayer((p) => ({ ...p, username: u.name }));
          if (typeof u?._gems_changed === "number") {
            setPlayer((p) => ({ ...p, gems: u._gems_changed }));
          }
        }}
        onPreferencesUpdated={(prefs) => {
          if (!prefs) return;
          setPreferences((prev) => ({ ...prev, ...prefs }));
          if (prefs?.spawn_interval_ms) setSpawnIntervalMs(prefs.spawn_interval_ms);
          if (prefs?.station_zoom) setStationZoom(prefs.station_zoom);
          // After purchasing premium the user may have a fresh expiry; flip flag
          // optimistically — auth-state refresh on next event will confirm.
          setIsPremium(true);
        }}
      />
      <MutualAidDialog
        open={mutualAidOpen}
        onOpenChange={setMutualAidOpen}
        currentUser={user}
        playerDonationPct={player.donation_pct || 0}
        playerHospitalFeePct={player.hospital_fee_pct || 0}
        onAllianceChanged={refreshAlliance}
        onPlayerSettingsChanged={(patch) => setPlayer((p) => ({ ...p, ...patch }))}
        onOpenAllianceHospitalPlacement={() => {
          setPlaceHospitalMode(true);
          setPlaceHospitalKind("alliance");
          setMutualAidOpen(false);
        }}
      />
      <SharedCallDialog
        open={!!activeShare}
        onOpenChange={(o) => !o && closeShareView()}
        share={activeShare}
        stations={stations}
        hospitals={hospitals}
        apparatusState={apparatusState}
        dispatches={dispatches}
        currentUserId={user?.user_id}
        onSendUnit={sendUnitToShare}
        onHostDispatch={handleDispatchUnit}
        onCancelDispatch={handleCancelDispatch}
        onQuickFinish={handleQuickFinish}
        onOpenTransport={(patient, share) => {
          // If the viewer is the HOST of this share, the patient lives on
          // their own local call — open the regular transport flow with no
          // `share` field so onPick takes the case-C path against the real
          // call.patients. Otherwise it's a helper view: pass a synthetic
          // call so onPick uses the helper's own local dispatch (sharedId).
          const isHost = !!share && share.requester_id === user?.user_id;
          const realCallId = share?.call?.id;
          const localCall = isHost ? calls.find((c) => c.id === realCallId) : null;
          if (isHost && localCall) {
            setTransportPayload({ patient, call: localCall });
            setTransportDialogOpen(true);
            return;
          }
          const c = share?.call || {};
          setTransportPayload({
            patient,
            call: {
              id: `share:${share.id}`,
              lat: c.lat,
              lng: c.lng,
              address: c.address || "",
              shareId: share.id,
            },
            share,
          });
          setTransportDialogOpen(true);
        }}
        onTransportArrestee={(cid, aid) => performJailTransport({ callId: cid, arresteeId: aid })}
        playerGems={player.gems}
      />
      <AlertDialog open={!!pendingBuild} onOpenChange={(o) => !o && cancelBuild()}>
        <AlertDialogContent
          data-testid="build-station-confirm"
          className="bg-zinc-950 border border-emerald-500/30 rounded-none max-w-md"
        >
          <AlertDialogHeader>
            <AlertDialogTitle className="mono uppercase tracking-widest text-emerald-300 text-base">
              Confirm Station Build
            </AlertDialogTitle>
            <AlertDialogDescription className="text-zinc-400 text-sm space-y-2">
              {pendingBuild && (
                <>
                  <div>
                    Build <span className="text-white font-semibold">{pendingBuild.name}</span>{" "}
                    near <span className="text-white">{pendingBuild.cityName}</span>?
                  </div>
                  <div className="mono text-[11px] text-zinc-500">
                    {pendingBuild.lat.toFixed(4)}°, {pendingBuild.lng.toFixed(4)}°
                  </div>
                  <div
                    data-testid="build-station-cost-line"
                    className="mono text-[13px] flex items-center gap-2 pt-1"
                  >
                    <span className="text-zinc-500 uppercase tracking-widest text-[10px]">Cost</span>
                    {pendingBuild.payWith === "gems" ? (
                      <span className="flex items-center gap-1 text-cyan-300">
                        <Diamond size={13} weight="fill" />
                        {pendingBuild.gCost.toLocaleString()} gems
                      </span>
                    ) : (
                      <span className="text-emerald-400">${pendingBuild.cost.toLocaleString()}</span>
                    )}
                  </div>
                </>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="gap-2">
            <AlertDialogCancel
              data-testid="build-station-cancel-btn"
              className="rounded-none mono uppercase text-[11px] tracking-widest border-white/15 text-zinc-300 hover:bg-white/5"
            >
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              data-testid="build-station-confirm-btn"
              onClick={confirmBuild}
              className="rounded-none mono uppercase text-[11px] tracking-widest bg-emerald-500 text-black hover:bg-emerald-400"
            >
              Confirm Build
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      <CallQueue
        calls={calls}
        stations={stations}
        apparatusState={apparatusState}
        dispatches={dispatchesWithHelpers}
        onCallClick={handleCallClick}
        onAutoDispatch={handleAutoDispatch}
        followUpQueue={followUpQueue}
      />
      <div
        className={`absolute top-[60px] left-72 sm:left-80 right-0 bottom-0 lg:bottom-56 z-10 ${helpPanelOpen ? "lg:right-80" : "right-0 lg:right-8"}`}
        data-testid="map-area"
      >
        <MapView
          stations={stations}
          hospitals={hospitals}
          allianceHospitals={allianceHospitals}
          showAllianceFacilities={showAllianceStations}
          onHospitalClick={(h) => {
            // Alliance-owned hospitals are tagged `kind: "alliance"` by MapView.
            const isAlliance = h.kind === "alliance";
            setDialogHospital(h);
            setViewOnlyMode(isAlliance);
            setViewOnlyOwnerName(null);
            setHospitalDialogOpen(true);
          }}
          onAllianceStationClick={async (s) => {
            try {
              const r = await allianceStationDetail(alliance.id, s.id);
              setDialogStation(r.station);
              setViewOnlyMode(true);
              setViewOnlyOwnerName(r.owner_name || null);
              setStationDialogOpen(true);
            } catch (_e) { /* silently ignore — likely 403/404 */ }
          }}
          calls={calls}
          dispatches={dispatches}
          onMapClick={handleMapClick}
          onStationClick={handleStationClick}
          onCallClick={handleCallClick}
          placeMode={placeMode || placeHospitalMode || placePoliceMode}
          mainStation={mainStation}
          mainStationZoom={stationZoom}
          allianceStations={allianceStations}
          showAllianceStations={showAllianceStations}
          myShares={myShares}
          showAllianceUnits={showAllianceUnits}
          sharedCallIds={sharedCallIds}
          allianceShares={shares}
          currentUserId={user?.user_id}
          onAllianceCallClick={handleOpenShare}
          cadIconsEnabled={preferences.cad_icons_enabled !== false}
          cadIconsShowFullName={!!preferences.cad_icons_show_full_name}
        />
        {placeHospitalMode && (
          <div className="absolute top-3 left-1/2 -translate-x-1/2 z-[1000] border border-pink-500/60 bg-pink-500/10 backdrop-blur px-3 py-1.5 mono text-[11px] uppercase tracking-widest text-pink-300">
            {placeHospitalKind === "alliance"
              ? `CLICK ANYWHERE IN THE US TO BUILD ALLIANCE HOSPITAL (${(alliance?.hospitals || []).length === 0 ? "FREE — first one!" : "$1,000,000 FROM BANK"})`
              : `CLICK ANYWHERE IN THE US TO BUILD HOSPITAL (${placeHospitalPayWith === "gems"
                ? `◆ ${gemCost(1_000_000).toLocaleString()} GEMS`
                : "$1,000,000"})`}
          </div>
        )}
        {placeMode && (
          <div className="absolute top-3 left-1/2 -translate-x-1/2 z-[1000] border border-emerald-500/60 bg-emerald-500/10 backdrop-blur px-3 py-1.5 mono text-[11px] uppercase tracking-widest text-emerald-300">
            CLICK ANYWHERE IN THE US TO BUILD FIRE STATION ({placeModePayWith === "gems"
              ? `◆ ${gemCost(stationCost(stations.filter(s => (s.kind || "fire") === "fire").length)).toLocaleString()} GEMS`
              : `$${stationCost(stations.filter(s => (s.kind || "fire") === "fire").length).toLocaleString()}`})
          </div>
        )}
        {placePoliceMode && (
          <div className="absolute top-3 left-1/2 -translate-x-1/2 z-[1000] border border-blue-500/60 bg-blue-500/10 backdrop-blur px-3 py-1.5 mono text-[11px] uppercase tracking-widest text-blue-300">
            CLICK ANYWHERE IN THE US TO BUILD POLICE STATION ({placePolicePayWith === "gems"
              ? `◆ ${gemCost(500_000).toLocaleString()} GEMS`
              : `$500,000`})
          </div>
        )}
        {stations.length === 0 && !placeMode && (
          <div className="absolute top-3 left-1/2 -translate-x-1/2 z-[1000] border border-yellow-500/60 bg-yellow-500/10 backdrop-blur px-3 py-1.5 mono text-[11px] uppercase tracking-widest text-yellow-300">
            BUILD YOUR FIRST FIRE STATION TO START RESPONDING
          </div>
        )}
      </div>
      {/* Bottom Fleet + Radio strip — DESKTOP only (≥1024px). On phones &
          tablets we hide this and surface FAB buttons that open them as
          full-screen drawers (see <MobileFabBar /> + <MobilePanelDrawer />). */}
      <div className="absolute left-80 right-0 bottom-0 h-56 border-t border-white/10 bg-[#0F1115] z-40 hidden lg:flex">
        <FleetPanel
          stations={stations}
          apparatusState={apparatusState}
          dispatches={dispatches}
          onStationClick={handleStationClick}
        />
        <RadioPanel entries={radioLog} />
      </div>

      {/* Mobile drawers — only mounted when open; share the same content
          components as desktop. */}
      {isSmallScreen && (
        <>
          <MobilePanelDrawer
            open={mobileFleetOpen}
            onClose={() => setMobileFleetOpen(false)}
            title="Fire Stations & Fleet"
            testId="mobile-drawer-fleet"
          >
            <div className="w-full h-full">
              <FleetPanel
                stations={stations}
                apparatusState={apparatusState}
                dispatches={dispatches}
                onStationClick={(s) => { setMobileFleetOpen(false); handleStationClick(s); }}
              />
            </div>
          </MobilePanelDrawer>
          <MobilePanelDrawer
            open={mobileRadioOpen}
            onClose={() => setMobileRadioOpen(false)}
            title="Radio Log"
            testId="mobile-drawer-radio"
          >
            <div className="w-full h-full">
              <RadioPanel entries={radioLog} />
            </div>
          </MobilePanelDrawer>
        </>
      )}
      {/* Mutual Aid — desktop rail on the right. Hidden on phones+tablets;
          replaced by an FAB button that opens the same panel as a drawer. */}
      {!isSmallScreen && (
        <HelpRequestsPanel
          open={helpPanelOpen}
          onToggle={toggleHelpPanel}
          alliance={alliance}
          shares={shares}
          currentUserId={user?.user_id}
          onRespond={handleRespondToShare}
          onOpenShare={handleOpenShare}
          onCloseShare={handleCloseShare}
          onOpenMutualAid={() => setMutualAidOpen(true)}
          showAllianceStations={showAllianceStations}
          onToggleAllianceStations={toggleAllianceStations}
          showAllianceUnits={showAllianceUnits}
          onToggleAllianceUnits={toggleAllianceUnits}
        />
      )}
      {isSmallScreen && (
        <MobilePanelDrawer
          open={mobileAidOpen}
          onClose={() => setMobileAidOpen(false)}
          title="Mutual Aid"
          testId="mobile-drawer-aid"
        >
          <HelpRequestsPanel
            open={true}
            onToggle={() => setMobileAidOpen(false)}
            alliance={alliance}
            shares={shares}
            currentUserId={user?.user_id}
            onRespond={(s) => { setMobileAidOpen(false); handleRespondToShare(s); }}
            onOpenShare={(s) => { setMobileAidOpen(false); handleOpenShare(s); }}
            onCloseShare={handleCloseShare}
            onOpenMutualAid={() => { setMobileAidOpen(false); setMutualAidOpen(true); }}
            showAllianceStations={showAllianceStations}
            onToggleAllianceStations={toggleAllianceStations}
            showAllianceUnits={showAllianceUnits}
            onToggleAllianceUnits={toggleAllianceUnits}
            mobile
          />
        </MobilePanelDrawer>
      )}

      {/* Floating action buttons (phones + tablets) */}
      <MobileFabBar
        onOpenFleet={() => setMobileFleetOpen(true)}
        onOpenRadio={() => setMobileRadioOpen(true)}
        onOpenAid={() => setMobileAidOpen(true)}
        hasAlliance={!!alliance}
        fleetActive={mobileFleetOpen}
        radioActive={mobileRadioOpen}
        aidActive={mobileAidOpen}
      />

      {/* "Rotate your phone" overlay (phones only, portrait) */}
      <RotatePhoneOverlay />

      {/* iOS Safari "Add to Home Screen" hint (auto-hides elsewhere) */}
      <IosInstallHint />

      {/* First-launch fullscreen prompt (mobile/tablet only, remembers choice) */}
      <LaunchFullscreenPrompt />
      <StationDialog
        open={stationDialogOpen}
        onOpenChange={(open) => {
          setStationDialogOpen(open);
          if (!open) { setViewOnlyMode(false); setViewOnlyOwnerName(null); }
        }}
        station={dialogStation}
        apparatusState={apparatusState}
        dispatches={dispatches}
        playerMoney={player.money}
        playerGems={player.gems}
        hasHospital={(hospitals && hospitals.length > 0) || (allianceHospitals && allianceHospitals.length > 0)}
        onPlayerUpdate={handlePlayerUpdate}
        readOnly={viewOnlyMode}
        ownerName={viewOnlyOwnerName}
      />
      <HospitalDialog
        open={hospitalDialogOpen}
        onOpenChange={(open) => {
          setHospitalDialogOpen(open);
          if (!open) { setViewOnlyMode(false); setViewOnlyOwnerName(null); }
        }}
        hospital={dialogHospital}
        player={player}
        onPlayerUpdate={handlePlayerUpdate}
        readOnly={viewOnlyMode}
        ownerName={viewOnlyOwnerName}
        allianceId={dialogHospital?.kind === "alliance" ? alliance?.id : null}
        canEditAllianceHospital={dialogHospital?.kind === "alliance" && (alliance?.viewer_rank === "chief" || alliance?.viewer_rank === "deputy")}
        onAllianceHospitalChanged={({ id, name }) => {
          // Optimistically update both the open dialog's hospital and the
          // allianceHospitals state used by the map / dropdowns.
          setDialogHospital((h) => (h && h.id === id ? { ...h, name } : h));
          setAllianceHospitals((prev) => prev.map((h) => (h.id === id ? { ...h, name } : h)));
        }}
      />
      <CallDialog
        open={callDialogOpen}
        onOpenChange={setCallDialogOpen}
        call={activeCall}
        stations={stations}
        hospitals={hospitals}
        apparatusState={apparatusState}
        dispatches={dispatchesWithHelpers}
        onDispatch={handleDispatchUnit}
        onCancelDispatch={handleCancelDispatch}
        onQuickFinish={handleQuickFinish}
        onOpenTransport={(patient, callForPatient) => {
          setTransportPayload({ patient, call: callForPatient });
          setTransportDialogOpen(true);
        }}
        onTransportArrestee={(cid, aid) => performJailTransport({ callId: cid, arresteeId: aid })}
        playerGems={player.gems}
        hasAlliance={!!alliance}
        isShared={
          !!(activeCall && shares.find(
            (s) => s.requester_id === user?.user_id && s.call?.id === activeCall.id
          ))
        }
        onShareWithAlliance={handleShareCall}
        followUpQueue={followUpQueue}
        onToggleFollowUp={toggleFollowUp}
      />
      <TransportDialog
        open={transportDialogOpen}
        onOpenChange={setTransportDialogOpen}
        patient={transportPayload?.patient || null}
        call={transportPayload?.call || null}
        medic={(() => {
          // Pick any Medic currently on scene for this call. For a helper
          // view of a shared call, the medic lives in MY local dispatches
          // keyed by sharedId.
          const pl = transportPayload;
          if (!pl?.call) return null;
          if (pl.share) {
            const d = dispatches.find(
              (x) => x.sharedId === pl.share.id && x.onScene && (x.apparatusType === "Medic" || x.apparatusType === "HEMS")
            );
            return d || null;
          }
          const d = dispatchesWithHelpers.find(
            (x) => x.callId === pl.call.id && x.onScene && (x.apparatusType === "Medic" || x.apparatusType === "HEMS")
          );
          return d || null;
        })()}
        onPick={async (h) => {
          const { patient, call, share } = transportPayload || {};
          await performTransport({ patient, call, hospital: h, share });
        }}
      />
    </div>
  );
}
